// v1.21.0 · Escultura de puntos de la portada: una misma nube de datos sueltos que se ordena en la forma de cada
//           servicio (percha, torre, equipo de personas, app, reloj de 48 h). Canvas 2D con proyección 3D propia:
//           sin librerías nuevas. Cada punto lleva un color con significado: celeste = dato, verde = en producción,
//           papel = estructura.

const TAU = Math.PI * 2;

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Reparte N puntos entre piezas según su peso. Cada pieza devuelve [x, y, z, color]
function sample(n, parts, seed) {
  const r = rng(seed);
  const total = parts.reduce((a, p) => a + p.w, 0);
  const out = new Float32Array(n * 4);
  for (let i = 0; i < n; i += 1) {
    let k = r() * total;
    let part = parts[0];
    for (const p of parts) { if ((k -= p.w) <= 0) { part = p; break; } }
    const [x, y, z, c] = part.fn(r);
    out.set([x, y, z, c], i * 4);
  }
  return out;
}

const lerp = (a, b, t) => a + (b - a) * t;
const seg = (r, a, b, c, j = 0.006) => {
  const t = r();
  return [lerp(a[0], b[0], t) + (r() - 0.5) * j, lerp(a[1], b[1], t) + (r() - 0.5) * j, lerp(a[2], b[2], t) + (r() - 0.5) * j, c];
};
const boxSurface = (r, cx, cy, cz, w, h, d, c) => {
  const f = Math.floor(r() * 6);
  const u = r() - 0.5;
  const v = r() - 0.5;
  const p = [[w * u, h * v, d / 2], [w * u, h * v, -d / 2], [w / 2, h * u, d * v], [-w / 2, h * u, d * v],
    [w * u, h / 2, d * v], [w * u, -h / 2, d * v]][f];
  return [cx + p[0], cy + p[1], cz + p[2], c];
};
const sphere = (r, cx, cy, cz, rad, c) => {
  const u = r() * 2 - 1;
  const a = r() * TAU;
  const s = Math.sqrt(1 - u * u);
  return [cx + rad * s * Math.cos(a), cy + rad * u, cz + rad * s * Math.sin(a), c];
};
const ring = (r, cx, cy, cz, rad, c, tilt = 0, j = 0.01) => {
  const a = r() * TAU;
  const y = Math.sin(a) * rad * Math.sin(tilt);
  return [cx + Math.cos(a) * rad + (r() - 0.5) * j, cy + y + (r() - 0.5) * j, cz + Math.sin(a) * rad * Math.cos(tilt) + (r() - 0.5) * j, c];
};

// 0 celeste (dato) · 1 verde (en producción) · 2 papel (estructura)
export function buildShapes(n) {
  const intro = sample(n, [
    { w: 7, fn: (r) => { const p = sphere(r, 0, 0, 0, 0.82 + (r() - 0.5) * 0.18, r() < 0.8 ? 0 : 2); return p; } },
    { w: 3, fn: (r) => { const p = sphere(r, 0, 0, 0, 1.0 + r() * 0.9, r() < 0.5 ? 2 : 0); return p; } },
  ], 11);

  // Retail: percha con productos (exhibición) y una tarjeta de precio
  const shelves = [-0.7, -0.22, 0.26, 0.74];
  const retailParts = [
    { w: 1.4, fn: (r) => seg(r, [-0.8, -0.95, 0], [-0.8, 0.95, 0], 2) },
    { w: 1.4, fn: (r) => seg(r, [0.8, -0.95, 0], [0.8, 0.95, 0], 2) },
    ...shelves.map((y) => ({ w: 1.2, fn: (r) => [lerp(-0.8, 0.8, r()), y, lerp(-0.22, 0.22, r()), 2] })),
  ];
  shelves.slice(0, 3).forEach((y, si) => {
    for (let k = 0; k < 6; k += 1) {
      const h = 0.14 + ((k * 7 + si * 3) % 5) * 0.045;
      const x = -0.66 + k * 0.265;
      retailParts.push({ w: 0.55, fn: (r) => boxSurface(r, x, y + h / 2 + 0.01, 0.02, 0.2, h, 0.2, (k + si) % 3 === 0 ? 1 : 0) });
    }
  });
  retailParts.push({ w: 0.6, fn: (r) => boxSurface(r, -0.2, 0.95, 0.15, 0.5, 0.18, 0.02, 1) });
  const retail = sample(n, retailParts, 21);

  // Telecom / ISP: torre en celosía, antenas y ondas de señal
  const top = 0.95;
  const legs = [[-0.42, -0.42], [0.42, -0.42], [0.42, 0.42], [-0.42, 0.42]];
  const telParts = legs.map(([x, z]) => ({ w: 1.2, fn: (r) => seg(r, [x, -1.0, z], [x * 0.12, top, z * 0.12], 2) }));
  for (let lv = 0; lv < 5; lv += 1) {
    const y0 = -1.0 + lv * 0.39;
    const y1 = y0 + 0.39;
    const s0 = lerp(1, 0.12, (y0 + 1) / (top + 1));
    const s1 = lerp(1, 0.12, (y1 + 1) / (top + 1));
    for (let e = 0; e < 4; e += 1) {
      const a = legs[e];
      const b = legs[(e + 1) % 4];
      telParts.push({ w: 0.28, fn: (r) => seg(r, [a[0] * s0, y0, a[1] * s0], [b[0] * s1, y1, b[1] * s1], 2) });
    }
  }
  [[0.13, 0.75, 0.1], [-0.12, 0.62, -0.06]].forEach(([x, y, z]) => telParts.push({ w: 0.45, fn: (r) => boxSurface(r, x, y, z, 0.05, 0.22, 0.12, 1) }));
  [0.32, 0.55, 0.78, 1.0].forEach((rad, i) => telParts.push({ w: 0.9 + i * 0.3, fn: (r) => ring(r, 0, top - 0.05, 0, rad, i % 2 ? 0 : 1, 0.12, 0.02) }));
  const telecom = sample(n, telParts, 31);

  // Talento humano: un equipo de cinco personas en arco, conectado
  const people = [[-0.9, 0.35, 0.82], [-0.45, -0.05, 0.92], [0, -0.25, 1.08], [0.45, -0.05, 0.92], [0.9, 0.35, 0.82]];
  const tParts = [];
  people.forEach(([x, z, s], i) => {
    const c = i === 2 ? 1 : 0;
    tParts.push({ w: 0.9 * s, fn: (r) => sphere(r, x, 0.32 * s, z, 0.13 * s, c) });
    tParts.push({ w: 2.0 * s, fn: (r) => {
      const t = r();
      const y = lerp(0.12, -0.75, t) * s;
      const rad = lerp(0.1, 0.27, Math.sqrt(t)) * s;
      const a = r() * TAU;
      return [x + Math.cos(a) * rad, y, z + Math.sin(a) * rad * 0.7, c === 1 ? 1 : 2];
    } });
    if (i < 4) {
      const [x2, z2, s2] = people[i + 1];
      tParts.push({ w: 0.35, fn: (r) => seg(r, [x, 0.32 * s, z], [x2, 0.32 * s2, z2], 0, 0.01) });
    }
  });
  tParts.push({ w: 0.9, fn: (r) => ring(r, 0, -0.85, 0.35, 1.05, 2, 1.45, 0.02) });
  const talento = sample(n, tParts, 41);

  // Apps, automatización e IA: un teléfono con pantallas y un lazo de automatización alrededor
  const ph = { w: 0.62, h: 1.25 };
  const appParts = [
    { w: 1.6, fn: (r) => { const t = r() * 4; const k = Math.floor(t); const u = t - k;
      const P = [[-ph.w / 2, -ph.h / 2], [ph.w / 2, -ph.h / 2], [ph.w / 2, ph.h / 2], [-ph.w / 2, ph.h / 2]];
      const a = P[k]; const b = P[(k + 1) % 4];
      return [lerp(a[0], b[0], u), lerp(a[1], b[1], u), 0, 2]; } },
  ];
  [[0.42, 0.18], [0.17, 0.18], [-0.08, 0.18], [-0.33, 0.12]].forEach(([y, h], i) => appParts.push({
    w: 0.7, fn: (r) => [lerp(-0.24, 0.24, r()), y - r() * h, 0.01, i === 1 ? 1 : 0] }));
  appParts.push({ w: 3.2, fn: (r) => {   // nudo toroidal (2,3): el proceso que se repite solo
    const t = r() * TAU;
    const R = 0.95; const rr = 0.32;
    const x = (R + rr * Math.cos(3 * t)) * Math.cos(2 * t);
    const z = (R + rr * Math.cos(3 * t)) * Math.sin(2 * t);
    const y = rr * Math.sin(3 * t);
    return [x, y * 1.4, z, Math.sin(t * 6) > 0.6 ? 1 : 0];
  } });
  const apps = sample(n, appParts, 51);

  // Demo en 48 horas: esfera de reloj con 48 marcas y sus agujas
  const dParts = [
    { w: 2.0, fn: (r) => ring(r, 0, 0, 0, 1.0, 2, Math.PI / 2, 0.01) },
    { w: 0.9, fn: (r) => ring(r, 0, 0, 0, 0.18, 1, Math.PI / 2, 0.02) },
    { w: 1.2, fn: (r) => seg(r, [0, 0, 0.01], [0.48, 0.45, 0.01], 1, 0.02) },
    { w: 1.0, fn: (r) => seg(r, [0, 0, 0.01], [-0.18, 0.62, 0.01], 0, 0.02) },
  ];
  for (let k = 0; k < 48; k += 1) {
    const a = (k / 48) * TAU;
    const long = k % 12 === 0;
    const r0 = long ? 0.74 : 0.84;
    dParts.push({ w: long ? 0.22 : 0.09, fn: (r) => seg(r, [Math.sin(a) * r0, Math.cos(a) * r0, 0], [Math.sin(a) * 0.95, Math.cos(a) * 0.95, 0], long ? 1 : 0, 0.012) });
  }
  const demo = sample(n, dParts, 61);

  return [intro, retail, telecom, talento, apps, demo];
}

const smooth = (t) => t * t * (3 - 2 * t);

/** Dibuja la escultura. progress: posición continua entre formas (0 = intro, 1 = retail…). */
export function createSculpture(canvas, { mobile = false, reduced = false } = {}) {
  const n = mobile ? 1500 : 2800;
  const shapes = buildShapes(n);
  const ctx = canvas.getContext('2d');
  const COLORS = [[93, 165, 216], [79, 163, 58], [214, 223, 225]];   // --test-fill, --live-fill, papel
  // Colas de cometa: solo 4 de cada 10 puntos dejan estela (sus últimas TRAIL posiciones en pantalla)
  const TRAIL = 10;
  const isTrail = (i) => (i * 37) % 10 < 4;
  const trailIdx = new Int32Array(n).fill(-1); let nt = 0;
  for (let i = 0; i < n; i += 1) if (isTrail(i)) { trailIdx[i] = nt; nt += 1; }
  const hist = new Float32Array(nt * TRAIL * 2); const tcol = new Uint8Array(nt);
  let head = 0; let filled = 0;
  const state = { progress: 0, target: 0, rotY: 0.5, clock: 0, mx: 0, my: 0, w: 0, h: 0, dpr: 1, offsetX: 0.3, offsetY: 0, scale: 0.34 };

  function resize() {
    const rect = canvas.getBoundingClientRect();
    state.dpr = Math.min(window.devicePixelRatio || 1, 2);
    state.w = rect.width; state.h = rect.height;
    canvas.width = Math.round(rect.width * state.dpr);
    canvas.height = Math.round(rect.height * state.dpr);
    const narrow = rect.width < 820;
    state.offsetX = narrow ? 0 : 0.17;
    state.offsetY = narrow ? -0.17 : 0;
    state.scale = narrow ? Math.min(rect.width, rect.height * 0.62) * 0.4 : Math.min(rect.width * 0.5, rect.height) * 0.36;
  }

  function frame(dt) {
    const { w, h, dpr } = state;
    state.progress = reduced ? Math.round(state.target) : state.progress + (state.target - state.progress) * Math.min(1, dt * 3.2);
    // Vaivén (±0,55 rad) en lugar de giro completo: la forma siempre se ve de frente o en tres cuartos
    if (!reduced) { state.clock += dt; state.rotY = 0.35 + Math.sin(state.clock * 0.32) * 0.55; }
    const p = Math.max(0, Math.min(shapes.length - 1, state.progress));
    const k = Math.min(shapes.length - 2, Math.floor(p));
    const raw = p - k;
    const t = smooth(Math.max(0, Math.min(1, (raw - 0.12) / 0.76)));
    const A = shapes[k]; const B = shapes[k + 1];
    // Durante el cambio los puntos se dispersan un poco (datos sueltos que se reordenan)
    const burst = Math.sin(t * Math.PI) * 0.45;
    const ry = state.rotY + state.mx * 0.35;
    const rx = -0.12 + state.my * 0.18;
    const cy = Math.cos(ry); const sy = Math.sin(ry); const cx = Math.cos(rx); const sx = Math.sin(rx);
    const S = state.scale; const ox = w * (0.5 + state.offsetX); const oy = h * (0.5 + state.offsetY);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const trails = !reduced && dt > 0;
    if (trails) { head = (head + 1) % TRAIL; filled = Math.min(TRAIL, filled + 1); } else filled = 0;
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < n; i += 1) {
      const j = i * 4;
      const bi = ((i * 7919) % n) * 4;   // pareja distinta en la forma siguiente: los puntos viajan
      let x = lerp(A[j], B[bi], t); let y = lerp(A[j + 1], B[bi + 1], t); let z = lerp(A[j + 2], B[bi + 2], t);
      if (burst > 0.001) {
        const nx = Math.sin(i * 12.9898) * 0.5; const ny = Math.sin(i * 78.233) * 0.5; const nz = Math.sin(i * 37.719) * 0.5;
        x += nx * burst; y += ny * burst; z += nz * burst;
      }
      const c = t < 0.5 ? A[j + 3] : B[bi + 3];
      // rotación y proyección
      const x1 = x * cy + z * sy; const z1 = -x * sy + z * cy;
      const y1 = y * cx - z1 * sx; const z2 = y * sx + z1 * cx;
      const persp = 3.2 / (3.2 + z2);
      const px = ox + x1 * S * persp; const py = oy - y1 * S * persp;
      const depth = Math.max(0, Math.min(1, (1.6 - z2) / 3.2));
      const col = COLORS[c] || COLORS[0];
      ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${(0.22 + depth * 0.6).toFixed(3)})`;
      const sz = (c === 1 ? 2.2 : 1.6) * persp;
      ctx.fillRect(px - sz / 2, py - sz / 2, sz, sz);
      const ti = trailIdx[i];
      if (trails && ti >= 0) { const o = (ti * TRAIL + head) * 2; hist[o] = px; hist[o + 1] = py; tcol[ti] = c; }
    }
    // Estelas: un trazo por color y por antigüedad (pocas llamadas a stroke), cada vez más tenue y delgado
    if (trails && filled > 1) {
      ctx.lineCap = 'round';
      for (let age = 0; age < filled - 1; age += 1) {
        const h0 = (head - age + TRAIL) % TRAIL; const h1 = (h0 - 1 + TRAIL) % TRAIL;
        const fade = 1 - age / (TRAIL - 1);
        for (let c = 0; c < COLORS.length; c += 1) {
          ctx.beginPath();
          for (let ti = 0; ti < nt; ti += 1) {
            if (tcol[ti] !== c) continue;
            const o0 = (ti * TRAIL + h0) * 2; const o1 = (ti * TRAIL + h1) * 2;
            const dx = hist[o0] - hist[o1]; const dy = hist[o0 + 1] - hist[o1 + 1];
            if (dx * dx + dy * dy < 0.04 || dx * dx + dy * dy > 900) continue;   // quieto, o salto brusco (resize, scroll de golpe)
            ctx.moveTo(hist[o0], hist[o0 + 1]); ctx.lineTo(hist[o1], hist[o1 + 1]);
          }
          const col = COLORS[c];
          ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${(0.5 * fade * fade).toFixed(3)})`;
          ctx.lineWidth = 0.4 + 1.4 * fade;
          ctx.stroke();
        }
      }
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  let raf = 0; let last = 0; let running = false;
  const loop = (ts) => {
    const dt = last ? Math.min(0.05, (ts - last) / 1000) : 0.016;
    last = ts;
    frame(dt);
    if (running) raf = requestAnimationFrame(loop);
  };
  resize();
  return {
    setProgress(v) { state.target = v; if (reduced) frame(0); },
    setPointer(x, y) { state.mx = x; state.my = y; },
    resize() { resize(); frame(0); },
    start() { if (running || reduced) { frame(0); return; } running = true; last = 0; raf = requestAnimationFrame(loop); },
    stop() { running = false; cancelAnimationFrame(raf); },
    draw() { frame(0); },
  };
}
